import { basicSort } from '@utils/sort';
import { styleDateRange } from '@utils/styles';
import { count, sum } from '@utils/summaries';
import { useReportLoad } from '@hooks/useReportLoad';
import { BaseReport } from '../BaseReport';
import { REPORT } from '@consts/reports';

interface ManufacturerBreakdownProps {
    dateRange: { startDate: string; endDate: string };
}

export const ManufacturerBreakdown = ({ dateRange }: ManufacturerBreakdownProps) => {
    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.MANUFACTURER_BREAKDOWN,
        params: { ...dateRange },
    });

    return (
        <BaseReport
            title={'Manufacturer Breakdown'}
            description={'This chart shows the number of cuts, and the % of your total cuts, by manufacturer for the date range selected above.'}
            note={
                ' Note: not all of Manufacturers are visible, as some are deselected by default. You can select or deselect Manufacturers to display on the graph by clicking on them in the legend below.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Manufacturer Name', 'Successful Cuts'],
                data: data.map((item) => [item.manufacturer, item.successfulCuts]),
                summaries: [count, sum],
                sort: [basicSort, basicSort],
            }}
            chartOptions={{
                chart: {
                    type: 'pie',
                    animation: false,
                    height: 300,
                },
                legend: {
                    layout: 'vertical',
                    align: 'center',
                    verticalAlign: 'middle',
                    padding: 20,
                    x: 120,
                    maxHeight: 200,
                    labelFormat: '{name}: ({y}) <b>[{percentage:.1f}]%</b>',
                },
                plotOptions: {
                    pie: {
                        center: ['40%', '50%'],
                        allowPointSelect: false,
                        cursor: 'pointer',
                        dataLabels: {
                            enabled: false,
                        },
                        showInLegend: true,
                    },
                    series: {
                        animation: {
                            duration: 0,
                        },
                    },
                },
                title: 'Manufacturer Breakdown',
                credits: { enabled: false },
                tooltip: {
                    pointFormat: '{point.name}: {point.y} <b>[{point.percentage:.1f}]%</b>',
                },
                series: [{ data: data.map((item) => ({ name: item.manufacturer, y: item.successfulCuts })) }],
                responsive: {
                    rules: [
                        {
                            condition: {
                                maxWidth: 600,
                            },
                            chartOptions: {
                                legend: {
                                    x: 0,
                                    verticalAlign: 'bottom',
                                    align: 'center',
                                    maxHeight: 85,
                                    padding: 0,
                                    margin: 0,
                                },
                                plotOptions: {
                                    pie: {
                                        center: ['50%', '50%'],
                                        size: 180,
                                    },
                                },
                            },
                        },
                        {
                            condition: {
                                maxWidth: 960,
                                minWidth: 601,
                            },
                            chartOptions: {
                                legend: {
                                    x: 130,
                                },
                                plotOptions: {
                                    pie: {
                                        center: ['38%', '45%'],
                                        size: 200,
                                    },
                                },
                            },
                        },
                    ],
                },
            }}
        />
    );
};
